<template>
  <v-row dense>
    <v-col
      v-if="layouts.length > 1 && $vuetify.breakpoint.xlOnly"
      cols="auto"
    >
      <draggable
        :list="dirtyLayouts"
        handle=".handle"
        :group="{ name: 'layouts' }"
        :invert-swap="true"
        :swap-treshold="0.6"
        :animation="100"
        @end="updateOrder"
      >
        <v-btn
          v-for="layout in dirtyLayouts"
          :key="layout.name"
          :value="layout.name"
          :outlined="dirtySelectedLayout.name !== layout.name"
          :color="dirtySelectedLayout.name === layout.name ? 'primary' : ''"
          :text="dirtySelectedLayout.name !== layout.name"
          class="mx-1"
          @click="dirtySelectedLayout = layout"
        >
          <v-icon
            small
            left
            class="handle"
          >
            fal fa-grip-lines
          </v-icon>
          {{ layout.name }}
        </v-btn>
      </draggable>
    </v-col>

    <v-col
      v-else-if="layouts.length > 1"
    >
      <v-menu
        v-model="menuVisible"
        offset-y
        max-height="300"
        :close-on-content-click="false"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            solo
            dense
            color="white"
            style="width: 175px; justify-content: space-between;"
            v-on="on"
          >
            {{ dirtySelectedLayout.name }}
            <v-icon small>
              fal fa-chevron-down
            </v-icon>
          </v-btn>
        </template>

        <v-card>
          <draggable
            :list="dirtyLayouts"
            handle=".handle"
            :group="{ name: 'layouts' }"
            :invert-swap="true"
            :swap-treshold="0.6"
            :animation="100"
            @end="updateOrder"
          >
            <v-list-item
              v-for="(layout, index) in dirtyLayouts"
              :key="index"
              dense
              :style="{
                fontWeight: layout.name === selectedLayout.name ? 'bold' : 'normal',
                color: layout.name === selectedLayout.name ? 'var(--v-primary-base)' : 'inherit',
                backgroundColor: layout.name === selectedLayout.name ? 'rgba(var(--v-primary-base), 0.1)' : 'transparent'
              }"
              @click="selectLayout(layout)"
            >
              <v-list-item-content class="pa-0">
                <v-list-item-title>
                  {{ layout.name }}
                </v-list-item-title>
              </v-list-item-content>

              <v-list-item-action>
                <v-icon
                  small
                  color="grey darken-1"
                  class="handle"
                >
                  fal fa-grip-lines
                </v-icon>
              </v-list-item-action>
            </v-list-item>
          </draggable>
        </v-card>
      </v-menu>
    </v-col>

    <v-col
      v-if="isAdmin && hasLayoutManagement"
      cols="auto"
    >
      <v-btn
        icon
        @click="$emit('edit-layout')"
      >
        <v-icon small>
          fa-light fa-gear
        </v-icon>
      </v-btn>
    </v-col>

    <v-col
      v-if="isAdmin && hasLayoutManagement"
      cols="auto"
    >
      <v-btn
        icon
        @click="$emit('add-layout')"
      >
        <v-icon small>
          fa-regular fa-plus
        </v-icon>
      </v-btn>
    </v-col>
  </v-row>
</template>

<script>
import { LayoutApiService } from '@/data/services'
import draggable from 'vuedraggable'
import { cloneDeep } from 'lodash'

export default {
  name: 'LayoutSelector',
  components: {
    draggable
  },
  props: {
    layouts: {
      type: Array,
      required: true
    },
    selectedLayout: {
      type: Object,
      required: true
    },
    layoutReady: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      menuVisible: false,
      submitting: false,
      dirtyLayouts: []
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    isAdmin() {
      return this.$can('manage-organisation')
    },
    hasLayoutManagement() {
      return this.user && this.user.organisation_settings?.has_layout_management
    },
    dirtySelectedLayout: {
      get: function () {
        return this.selectedLayout
      },
      set: function (value) {
        this.$emit('update:selectedLayout', value)
      }
    }
  },
  watch: {
    layouts() {
      this.dirtyLayouts = cloneDeep(this.layouts)
    }
  },
  created() {
    this.dirtyLayouts = cloneDeep(this.layouts)
  },
  methods: {
    selectLayout(layout) {
      this.dirtySelectedLayout = layout
      this.menuVisible = false
      this.$emit('layout-ready', false)
    },
    updateOrder() {
      this.dirtyLayouts = this.dirtyLayouts.map((layout, index) => {
        return {
          ...layout,
          order: index + 1
        }
      })
      this.saveReorder()
    },
    async saveReorder() {
      if (!this.hasLayoutManagement) return

      try {
        this.submitting = true

        const data = this.dirtyLayouts.map((layout, index) => {
          return {
            id: layout.id,
            order: index + 1
          }
        })

        await LayoutApiService.updateOrder(data)
        this.$store.dispatch(this.$storeActions.APP_FETCH_LAYOUTS)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.layout_order.success') || 'Layout order updated successfully',
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_attach.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.handle {
  cursor: move;
}
</style>
